import React from "react";
import styled, { keyframes, css } from "styled-components";
import InfiniteScroll from "react-infinite-scroll-component";
import ProductItem from "./product.item";
import ProductDetail from "./product.detail";
import ProductLoader from "./Loader/item.loader";
import InputSearch from "components/Form/InputSearch";
import MainLayout from "components/Layouts/MainLayout";
import ConfirmationModal from "components/Confirmation";
import NoPhoto from "assets/images/no-product-photo.svg";
import useRedux from "redux/useRedux";
import useDebounce from "hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import { notify } from "helper/general";
import { Box, Grid, Stack, CircularProgress } from "@mui/material";
import { YellowCircleCheckIcon } from "assets/icons";
import { ArrowLeftIcon, CartIcon, RefreshIcon } from "assets/icons";
import { Button } from "components/styled/button.styled";
import { NormalText } from "components/styled/text.styled";
import { getCategoryList } from "redux/product";
import {
  getTotalProduct,
  addToCart,
  IProduct,
  IAdditional,
} from "redux/cart/cart.reducers";
import {
  getProductList,
  ProductResponseProps,
  ProductCategoryListInterface,
} from "redux/product";

const PRODUCTS_PER_PAGE = 10;
const PULL_TO_REFRESH_THRESHOLD = 70;

const ProductPage = () => {
  const navigate = useNavigate();
  const {
    thunkDispatch,
    storeState: { Cart, AppOption },
  } = useRedux();

  const [activeCategory, setActiveCategory] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [showDetail, setShowDetail] = React.useState(false);
  const [addSuccess, setAddSuccess] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [cartPayload, setCartPayload] = React.useState<IProduct>();
  const [selectedProduct, setSelectedProduct] = React.useState<any>();
  const [products, setProducts] = React.useState<ProductResponseProps>();
  const [allProducts, setAllProducts] = React.useState<ProductResponseProps>();
  const [categories, setCategories] =
    React.useState<ProductCategoryListInterface[]>();
  const searchText = useDebounce({ value: search, delay: 500 });
  
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);

  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [pullPosition, setPullPosition] = React.useState(0);
  const touchStartY = React.useRef(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const handleAddToCart = (props: IProduct) => {
    thunkDispatch(addToCart(props))
      .unwrap()
      .then(() => setAddSuccess(true));
  };

  const fetchAllProduct = () => {
    return thunkDispatch(
      getProductList({
        store_id: AppOption?.selected_store?.id ?? 0,
        category_id: 0,
        keyword: searchText.toLowerCase(),
        limit: 0,
        offset: 0,
      })
    )
      .unwrap()
      .then((res) => {
        if (res && res.status === "success") {
          const productsData = res.data.data;
          const sortedProducts = [...productsData].sort((a, b) => {
            const aIsOutOfStock = a.is_stock && a.stock === 0;
            const bIsOutOfStock = b.is_stock && b.stock === 0;
            return (aIsOutOfStock ? 1 : 0) - (bIsOutOfStock ? 1 : 0);
          });
          setAllProducts({ ...res.data, data: sortedProducts });
        }
      });
  };

  const fetchProduct = (currentPage: number, categoryId: number, isRefresh = false) => {
    if (currentPage === 1 && !isRefresh) {
      setLoading(true);
    }
    if (currentPage > 1) {
        setLoadingMore(true);
    }

    return thunkDispatch(
      getProductList({
        store_id: AppOption?.selected_store?.id ?? 0,
        category_id: categoryId,
        keyword: searchText.toLowerCase(),
        limit: 0,
        offset: (currentPage - 1) * PRODUCTS_PER_PAGE,
      })
    )
    .unwrap()
    .then((res) => {
        if (res && res.status === "success") {
            const newProducts = res.data.data;
            const sortedProducts = [...newProducts].sort((a, b) => {
              const aIsOutOfStock = a.is_stock && a.stock === 0;
              const bIsOutOfStock = b.is_stock && b.stock === 0;
              return (aIsOutOfStock ? 1 : 0) - (bIsOutOfStock ? 1 : 0);
            });

            setProducts((prevProducts) => {
                const existingData = (currentPage === 1 || isRefresh) ? [] : prevProducts?.data || [];
                return {
                    ...res.data,
                    data: [...existingData, ...sortedProducts],
                };
            });

            setHasMore(newProducts.length === PRODUCTS_PER_PAGE);
        } else if (res && res.status === "error") {
            notify("error", res.error.response?.data?.message || res.error.message);
        }
    })
    .catch((err) => {
        console.log("Error fetching products", err);
        notify("error", "Gagal memuat produk.");
    })
    .finally(() => {
        setLoading(false);
        setLoadingMore(false);
        if (isRefresh) {
            setIsRefreshing(false);
            setPullPosition(0);
        }
    });
  };
  
  const fetchMoreProducts = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProduct(nextPage, activeCategory);
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setPage(1);
    setHasMore(true);
    await Promise.all([
        fetchProduct(1, activeCategory, true), 
        fetchAllProduct()
    ]);
  };
  
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      touchStartY.current = e.targetTouches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartY.current === 0 || isRefreshing) return;

    const currentY = e.targetTouches[0].clientY;
    const pullDistance = currentY - touchStartY.current;

    if (pullDistance > 0) {
      setPullPosition(pullDistance);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartY.current === 0) return;
    
    if (pullPosition > PULL_TO_REFRESH_THRESHOLD && !isRefreshing) {
      handleRefresh();
      setPullPosition(50); 
    } else {
      setPullPosition(0);
    }
    touchStartY.current = 0;
  };

  React.useEffect(() => {
    setPage(1);
    setHasMore(true);
    setProducts(undefined);
    fetchProduct(1, activeCategory);
    if(searchText) fetchAllProduct();
  }, [activeCategory, searchText]);

  React.useEffect(() => {
    thunkDispatch(getCategoryList())
      .unwrap()
      .then((res) => {
        if (res && res.status === "success") {
          setCategories(res.data.data);
        }
      });
    fetchAllProduct();
  }, []);

  React.useEffect(() => {
    thunkDispatch(getTotalProduct()).unwrap();
  }, [Cart, thunkDispatch]);

  const availableCategories = React.useMemo(() => {
      if (!categories || !allProducts) return [];
      const filtered = categories.filter((cat) => 
          allProducts.data.some(prod => prod.category.id === cat.id)
      );
      return [{id: 0, subscategory_name: "All"}, ...filtered];
  }, [categories, allProducts]);

  return (
    <MainLayout hideBackgroundImage hasNavbar>
        <ConfirmationModal
            open={addSuccess}
            icon={YellowCircleCheckIcon}
            titleStyle={{ margin: "15px 0" }}
            title={"Produk berhasil di tambahkan"}
            handleClose={() => setAddSuccess(false)}
            handleConfirm={() => null}
            noAction
        />
        
        <PullToRefreshWrapper
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <RefreshIndicator
                style={{
                    opacity: pullPosition > 0 ? 1 : 0,
                    transform: `translateY(${pullPosition > 50 ? (pullPosition / 5) : pullPosition - 40}px)`, 
                    transition: !isRefreshing && touchStartY.current === 0 ? 'all 0.3s ease' : 'none',
                }}
            >
                <IconContainer isRefreshing={isRefreshing}>
                    <img 
                        src={RefreshIcon}
                        alt="Refresh"
                        style={{
                            width: '34px',
                            height: '34px',
                            transform: !isRefreshing ? `rotate(${pullPosition * 3}deg)` : 'none',
                        }}
                    />
                </IconContainer>
            </RefreshIndicator>

            <ContentWrapper
                 style={{
                    transform: `translateY(${isRefreshing ? 50 : Math.min(pullPosition, PULL_TO_REFRESH_THRESHOLD + 40)}px)`,
                    transition: !isRefreshing && touchStartY.current === 0 ? 'transform 0.3s ease' : 'none',
                }}
            >
                <Box width={"100%"} padding={"15px 16px"}>
                    <Grid container direction={"row"}>
                        <div onClick={() => navigate("/")} style={{ width: "10%" }}>
                            <img
                                src={ArrowLeftIcon}
                                alt={"arrow_left_icon"}
                                style={{ cursor: "pointer" }}
                            />
                        </div>
                        <div
                            style={{
                                width: "90%",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                display: "flex",
                            }}
                        >
                            <NormalText
                                fontWeight="bold"
                                fontSize={"20px"}
                                textAlign={"center"}
                                margin={"0 0 0 -60px"}
                            >
                                Produk
                            </NormalText>
                        </div>
                    </Grid>
                </Box>
                <Box width={"100%"} padding={"0 15px"} margin={"10px 0 0 0"}>
                    <InputSearch
                        placeholder="Cari produk pilihanmu"
                        onChange={(e: any) => setSearch(e.target.value)}
                    />
                </Box>

                <Box margin={"11px 0 0 0"}>
                    <Navigation ref={scrollRef}>
                        <UnorderList>
                            {availableCategories.map((item, index) => (
                              <ListItem
                                  key={index}
                                  active={item.id === activeCategory}
                                  onClick={() => setActiveCategory(item.id)}
                              >
                                  {item.subscategory_name}  
                              </ListItem>
                            ))}
                        </UnorderList>
                    </Navigation>
                </Box>

                <ContainerProduct
                    id="scrollableProductContainer"
                    ref={containerRef}
                    cartExist={Cart?.total_product > 0}
                >
                    {loading ? (
                        <ProductLoader />
                    ) : (
                        <InfiniteScroll
                            dataLength={products?.data?.length || 0}
                            next={fetchMoreProducts}
                            hasMore={hasMore}
                            loader={
                                <Box display="flex" justifyContent="center" paddingY={2}>
                                    <CircularProgress />
                                </Box>
                            }
                            scrollableTarget="scrollableProductContainer"
                        >
                            <Grid container spacing={1}>
                                {products?.data?.map((product) => (
                                    <Grid item xs={6} key={`${product.product_id}-${product.product_name}`}>
                                    <ProductItem
                                      productId={product.product_id}
                                      name={product.product_name}
                                      product_price={Number(product.product_price)}
                                      price_after_discount={
                                        product.product_price_after_discount &&
                                          Number(product.product_price_after_discount) > 0
                                          ? Number(product.product_price_after_discount)
                                          : undefined
                                      }
                                      image={
                                        product.product_image_main.length === 0
                                          ? NoPhoto
                                          : product.product_image_main
                                      }
                                      customize={product.is_customizable}
                                      stock={product.stock}
                                      is_stock={product.is_stock}
                                      discount={Number(product.discount)}
                                      discount_type={product.discount_type}
                                      onBoxClick={() => {
                                        setSelectedProduct(product);
                                        setCartPayload({
                                          category_name: product?.category?.name ?? "",
                                          category_id: product?.category?.id ?? "",
                                          is_customizable: product.is_customizable ?? false,
                                          product_image:
                                            product.product_image_main.length === 0
                                              ? NoPhoto
                                              : product.product_image_main ?? "",
                                          product_name: product.product_name ?? "",
                                          price:
                                            product.product_price_after_discount &&
                                              Number(product.product_price_after_discount) > 0
                                              ? Number(product.product_price_after_discount)
                                              : Number(product.product_price),
                                          product_id: product.product_id ?? "",
                                          qty: 1,
                                          additionals: [],
                                          original_price: Number(product.product_price),
                                          discount: Number(product.discount ?? 0),
                                          discount_type: product.discount_type ?? "",
                                        });
                                        setShowDetail(true);
                                      }}
                                      onButtonClick={() => {
                                        setSelectedProduct(product);
                                        setCartPayload({
                                          category_name: product?.category?.name ?? "",
                                          category_id: product?.category?.id ?? "",
                                          is_customizable: product.is_customizable ?? false,
                                          product_image: product.product_image_main ?? "",
                                          product_name: product.product_name ?? "",
                                          price:
                                            product.product_price_after_discount &&
                                              Number(product.product_price_after_discount) > 0
                                              ? Number(product.product_price_after_discount)
                                              : Number(product.product_price),
                                          product_id: product.product_id ?? "",
                                          qty: 1,
                                          additionals: [],
                                        });
                                        setShowDetail(true);
                                      }}
                                    />

                                    </Grid>
                                ))}
                            </Grid>
                        </InfiniteScroll>
                    )}
                    {!loading && products?.data?.length === 0 && (
                        <Box
                          sx={{
                            height: "calc(100vh - 280px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                            <Stack>
                                <NormalText
                                  textAlign="center"
                                  fontWeight="bold"
                                  fontSize={"16px"}
                                >
                                  Oops!
                                </NormalText>
                                <NormalText textAlign="center" fontSize="12px">
                                  Tidak ada produk ditemukan{" "}
                                  {searchText ? `dengan kata kunci "${searchText}"` : ""}
                                </NormalText>
                            </Stack>
                        </Box>
                    )}
                </ContainerProduct>
            </ContentWrapper>
        </PullToRefreshWrapper>
        
        {Cart?.items?.length > 0 && (
          <CartBox>
            <Button
                width={"100%"}
                variant={"primary"}
                margin={"10px 0 0 0"}
                borderRadius={"8px"}
                style={{ letterSpacing: "0.5px" }}
                onClick={() => navigate("/cart")}
            >
              <Stack
                  textAlign={"center"}
                  alignItems={"center"}
                  direction={"row"}
                  justifyContent={"center"}
                  spacing={1}
              >
                  <img src={CartIcon} alt={"cart_ic"} style={{ width: "19px" }} />
                  <div>Cart ({Cart.total_product})</div>
              </Stack>
            </Button>
          </CartBox>
        )}

        {showDetail && selectedProduct && cartPayload && (
          <ProductDetail
              productId={cartPayload?.product_id ?? 0}
              name={cartPayload?.product_name ?? ""}
              category={cartPayload?.category_name ?? ""}
              customizable={cartPayload?.is_customizable ?? false}
              price={Number(selectedProduct.product_price)}
              price_after_discount={
                selectedProduct.product_price_after_discount &&
                Number(selectedProduct.product_price_after_discount) > 0
                  ? Number(selectedProduct.product_price_after_discount)
                  : undefined
              }
              discount={Number(selectedProduct.discount ?? 0)}
              discount_type={selectedProduct.discount_type}
              image={cartPayload?.product_image ?? ""}
              counterProps={{
                value: cartPayload?.qty,
                onIncrease: () => {
                  if (cartPayload && cartPayload.qty) {
                    setCartPayload({
                      ...cartPayload,
                      qty: cartPayload.qty + 1,
                    });
                  }
                },
                onDecrease: () => {
                  if (cartPayload && cartPayload.qty) {
                    let quantity = cartPayload.qty;
                    setCartPayload({
                      ...cartPayload,
                      qty: quantity === 1 ? 1 : cartPayload.qty - 1,
                    });
                  }
                },
              }}
              onAdd={(selected: IAdditional[]) => {
                if (cartPayload) {
                  handleAddToCart({
                    ...cartPayload,
                    additionals: selected,
                    original_price: Number(selectedProduct.product_price),
                    discount: Number(selectedProduct.discount ?? 0),
                    discount_type: selectedProduct.discount_type ?? "",
                  });
                }
                setShowDetail(false);
                setSelectedProduct(null);
              }}
              onClose={() => {
                setShowDetail(false);
                setSelectedProduct(null);
              }}
          />
        )}
    </MainLayout>
  );
};

const PullToRefreshWrapper = styled.div`
    position: relative;
    width: 100%;
    overflow: hidden;
`;

const ContentWrapper = styled.div`
    width: 100%;
    background-color: #FFFFFF;
`;

const RefreshIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  height: 40px;
`;

const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const IconContainer = styled.div<{ isRefreshing: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    transition: transform 0.2s linear;
    animation: ${({ isRefreshing }) =>
      isRefreshing
        ? css`${rotateAnimation} 1s linear infinite`
        : 'none'};
  }
`;

const CartBox = styled(Box)`
  background: white;
  position: absolute;
  padding: 0 20px;
  bottom: 100px;
  width: 100%;
  z-index: 2;

  @media only screen and (min-width: 1024px) {
    bottom: 100px;
  }
`;

interface ContainerProps {
  cartExist?: boolean;
}

const ContainerProduct = styled(Box)<ContainerProps>`
  margin: 10px 0 0 0;
  padding: 0; 
  width: 100%;
  overflow-y: auto;
  position: relative;
  background-color: white;
  height: ${(props) =>
    props.cartExist ? "calc(100vh - 280px)" : "calc(100vh - 225px)"};

  padding-bottom: 100px;
  box-sizing: border-box;

  @media only screen and (min-width: 1024px) {
    height: ${(props) =>
      props.cartExist ? "calc(100vh - 280px)" : "calc(100vh - 225px)"};
  }

  & .MuiGrid-container {
    padding: 0 15px;
  }
`;

const Navigation = styled.div`
  width: 100%;
  transition: ease-in-out 0.5s;
  margin-bottom: -5px;
  text-align: left;
  align-items: center;
  position: relative;
  overflow-y: hidden;
  overflow-x: auto;
  scroll-behavior: smooth;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const UnorderList = styled.ul`
  list-style: none;
  display: flex;
  align-items: center;
  padding: 0 16px;
  width: 100%;
  white-space: nowrap;
  gap: 8px;
`;

interface ListProps {
  active?: boolean;
  background?: string;
}

const ListItem = styled.li<ListProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 14px;
  font-family: "D-DIN", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 1px solid;

  background-color: ${(props) => (props.active ? "#FFC535" : "#FFFFFF")};
  color: ${(props) => (props.active ? "black" : "black")};
  border-color: ${(props) => (props.active ? "#FFC535" : "#E0E0E0")};
  font-weight: ${(props) => (props.active ? "bold" : "normal")};

  &:hover {
    background-color: ${(props) => (props.active ? "#FFD66A" : "#F5F5F5")};
  }
`;

export default ProductPage;